/** @jsxImportSource theme-ui @jsxRuntime classic */
import { AnchorSection, FeaturedEvents, Box } from '@tishman/components';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useMemo } from 'react';

import type { ComponentPropsWithoutRef } from 'react';

const RINK_FEATURED_EVENTS_QUERY = graphql`
  query RinkFeaturedEvents {
    featuredEvents: sanityAttractionRink(
      _id: { eq: "theRink" }
      _type: { eq: "attraction.rink" }
    ) {
      featuredEvents {
        __typename
        ... on SanityEventTreeLighting {
          startEndDateTime {
            formattedStartDateTime: startDateTime(formatString: "MMM D, h a")
            formattedEndDateTime: endDateTime(formatString: "MMM D, h a")
          }
          admissionType
          category
          location {
            title
          }
          photo {
            caption
            alt
            asset {
              gatsbyImageData(width: 1600, layout: CONSTRAINED)
            }
          }
          id
          type: _type
          titleAndSlug {
            slug {
              current
            }
            title
          }
        }
        ... on SanityEvent {
          startEndDateTime {
            formattedStartDateTime: startDateTime(formatString: "MMM D, h a")
            formattedEndDateTime: endDateTime(formatString: "MMM D, h a")
          }
          admissionType
          category
          location {
            title
          }
          photo {
            caption
            alt
            asset {
              gatsbyImageData(width: 1600, layout: CONSTRAINED)
            }
          }
          id
          type: _type
          titleAndSlug {
            slug {
              current
            }
            title
          }
        }
      }
    }
  }
`;

const RinkFeaturedEventsBlock = (
  props: ComponentPropsWithoutRef<typeof AnchorSection>,
): React.JSX.Element | null => {
  const { featuredEvents } = useStaticQuery<Queries.RinkFeaturedEventsQuery>(
    RINK_FEATURED_EVENTS_QUERY,
  );

  const events = featuredEvents?.featuredEvents;
  const featuredData = useMemo(() => {
    if (!events) throw new Error('Expected valid Event data');

    return events.map((event) => {
      if (!event) throw new Error('Expected valid object');
      if (
        event.__typename !== 'SanityEvent' &&
        event.__typename !== 'SanityEventTreeLighting'
      ) {
        throw new Error('Expected `SanityEvent` or `SanityEventTreeLighting`');
      }
      if (!event?.titleAndSlug?.title) throw new Error('Expected event title');
      if (!event?.titleAndSlug?.slug?.current)
        throw new Error('Expected event slug');
      if (!event?.id) throw new Error('Expected event id');
      if (!event?.location?.title) throw new Error('Expected event location');
      if (!event?.category) throw new Error('Expected event category');
      if (!event?.admissionType)
        throw new Error('Expected event admissionType');
      if (!event?.startEndDateTime?.formattedStartDateTime)
        throw new Error('Expected event formattedStartDateTime');
      if (!event?.startEndDateTime?.formattedEndDateTime)
        throw new Error('Expected event formattedEndDateTime');
      const { photo } = event;
      const image = {
        gatsbyImageData: photo?.asset?.gatsbyImageData,
        alt: photo?.alt || '',
      };
      return {
        category: event.category,
        title: event.titleAndSlug.title,
        url: `/events/${event.titleAndSlug.slug.current}`,
        formattedStartDateTime: event.startEndDateTime.formattedStartDateTime,
        formattedEndDateTime: event.startEndDateTime.formattedEndDateTime,
        location: { title: event.location.title },
        admissionType: event.admissionType,
        ...image,
      };
    });
  }, [events]);

  return featuredData.length ? (
    <AnchorSection data-section="RinkFeaturedEvents" {...props}>
      <Box
        sx={{
          py: [4, 9],
          pl: [4, 0],
          mx: 'auto',
          maxWidth: 1320,
        }}
      >
        <FeaturedEvents data={featuredData} />
      </Box>
    </AnchorSection>
  ) : null;
};

export default RinkFeaturedEventsBlock;
